import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { getSaleDepositReport } from "../../services";
import {
  SaleDepositReportFilter,
  SaleDepositResponse,
  SaleDepositReviewStatusFilter,
  SaleDepositTotals,
} from "../../types/sale-deposit.type";
import { SaleCenterClosingSummary } from "../../types/sale-center-closing.type";
import { formatNumber, numberWithCommas } from "../../utils";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import ReportPagination from "../Report/ReportList/Pagination";
import SaleDepositFilter, { isReviewStatusFilter } from "./Filter";
import TransferAmountText from "./TransferAmountText";
import TransferDiffText from "./TransferDiffText";
import DepositNoteModal from "./DepositNoteModal";
import TransferSlipsModal from "./TransferSlipsModal";
import ExpenseDetailModal from "./ExpenseDetailModal";
import OutstandingQrModal from "./OutstandingQrModal";
import UncollectedCashModal from "./UncollectedCashModal";
import FooterSummary from "./FooterSummary";
import NetOfNotesHint from "./NetOfNotesHint";
import NoteCell from "./NoteCell";
import ReviewCheckboxCell from "./ReviewCheckboxCell";
import SelectRowCheckboxCell from "./SelectRowCheckboxCell";
import AddSlipsDialog from "./AddSlipsDialog";
import BulkReviewAction from "./BulkReviewAction";
import TableSkeletonRows from "./TableSkeletonRows";
import SaleCenterClosingPanel from "./SaleCenterClosingPanel";
import { DepositReviewPatch, DepositReviewSide, useDepositActions } from "./useDepositActions";
import {
  DATE_COLUMN_WIDTH,
  SELECT_COLUMN_WIDTH,
  STICKY_BODY_Z_INDEX,
  STICKY_HEADER_Z_INDEX,
  stickyColumnSx,
} from "./tableLayout";
import EditRequestCell from "../SaleDepositEditRequest/EditRequestCell";
import EditRequestDialog from "../SaleDepositEditRequest/EditRequestDialog";
import {
  EditRequestOffer,
  editRequestOffer,
} from "../SaleDepositEditRequest/editRequest.utils";
import { useDepositEditRequests } from "../SaleDepositEditRequest/useDepositEditRequests";
import { DEPOSIT_REPORT_PARAM } from "./depositReportUrl";
import {
  callerReviewSide,
  reviewBlockedReason,
  selectionBlockedReason,
} from "./reviewGate";
import {
  ClosingBranchDay,
  resolveClosingBranchDay,
} from "./saleCenterClosing.utils";
import {
  CASH_IN_HAND_HINT,
  CASH_IN_HAND_LABEL,
  formatCashInHand,
  normalizeTransferSlips,
  physicalCash,
} from "./transfer.utils";

/**
 * Columns every reader sees. The selection column rides on top for a reviewer who owns a
 * ตรวจแล้ว box, and the จัดการ column for the cohort that may raise a คำขอแก้ไขยอดโอน.
 */
const BASE_COLUMN_COUNT = 18;
const LIMIT = 10;

/** The จัดการ column carries the row's overflow menu, the same as every other table here. */
const ROW_ACTIONS_HEADER = "จัดการ";

// A checkbox column has no visible header text, and a screen reader on a row of eighteen
// figures needs to be told which route it is about to sign off.
const SELECT_ALL_LABEL = "เลือกทุกสายส่งในหน้านี้";
const selectRowLabel = (routeCode: string) => `เลือกสายส่ง ${routeCode}`;

const formatDay = (value: string | null | undefined) =>
  value ? dayjs(value).format("DD/MM/YYYY") : "-";

const API_DATE_FORMAT = "YYYY-MM-DD";

/** A hand-edited or stale query param must not seed the picker with an invalid day. */
const parseDateParam = (value: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

interface SaleDepositReportTableProps {
  /**
   * Pins the table to ONE branch-day — the /report/deposit/:branchCode/:businessDate
   * drill-down, where the address bar decides the scope. Absent on the standalone report,
   * where the filter row does.
   */
  branchDay?: ClosingBranchDay;
}

export default function SaleDepositReportTable({
  branchDay,
}: SaleDepositReportTableProps) {
  const [searchParams] = useSearchParams();
  const role = useUserRole();
  const { isHeadOfficeAccounting, isMaster } = role;
  const canSelectBranch = Boolean(isHeadOfficeAccounting || isMaster);
  const canEditSlipAmount = featureAccess.saleDepositEditAmount(role);
  const canReviewAsHo = featureAccess.saleDepositReviewAsHo(role);
  const canReviewAsBo = featureAccess.saleDepositReviewAsBo(role);
  const canUploadSlipOnBehalf = featureAccess.saleDepositUploadSlipOnBehalf(role);
  const canEditExpenses = featureAccess.saleDepositEditExpenses(role);
  const canRequestEdit = featureAccess.saleDepositRequestEdit(role);
  // Selecting rows only leads somewhere for a reviewer who owns one of the two ตรวจแล้ว
  // boxes: the bulk write sets the caller's own side, and the server picks that side from
  // the token. Anyone else would get a column of checkboxes with no action behind it.
  const canBulkReview = canReviewAsHo || canReviewAsBo;
  // Which of the two ตรวจแล้ว columns this caller's own ticks land in — and therefore the
  // side a row must not already be signed on for selecting it to change anything.
  const reviewSide = callerReviewSide(canReviewAsHo);

  // A pinned branch-day is the scope, so it outranks every date/branch query param: the
  // drill-down carries the SUMMARY's filters in its query string as a return ticket, and the
  // summary's date range is normally wider than the one day being looked at.
  const pinnedDate = branchDay
    ? dayjs(branchDay.businessDate).startOf("day")
    : null;

  // Preset filters from the HO Dashboard ดูการโอน drill-down and from the branch-day summary
  // (query params). The range is always bounded — the report opens on today rather than on
  // every business date on record.
  const [dateFrom, setDateFrom] = useState<Dayjs>(
    () =>
      pinnedDate ??
      parseDateParam(searchParams.get(DEPOSIT_REPORT_PARAM.dateFrom)) ??
      dayjs().startOf("day"),
  );
  const [dateTo, setDateTo] = useState<Dayjs>(
    () =>
      pinnedDate ??
      parseDateParam(searchParams.get(DEPOSIT_REPORT_PARAM.dateTo)) ??
      dayjs().startOf("day"),
  );
  const [selectedBranch, setSelectedBranch] = useState<string>(() => {
    if (branchDay) return branchDay.branchCode;
    return canSelectBranch
      ? searchParams.get(DEPOSIT_REPORT_PARAM.branchCode) || "all"
      : "all";
  });
  const [selectedRoute, setSelectedRoute] = useState<string>(
    () => searchParams.get(DEPOSIT_REPORT_PARAM.routeCode) || "all",
  );
  // Seeded from the query string so a drill-down keeps the review filter the summary row was
  // counted under — otherwise the branch-day would list routes its own figures never included.
  const [status, setStatus] = useState<SaleDepositReviewStatusFilter>(() => {
    const requested = searchParams.get(DEPOSIT_REPORT_PARAM.status);
    return isReviewStatusFilter(requested) ? requested : "all";
  });

  // Rows ticked in the selection column, for the bulk ตรวจแล้ว above the table.
  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<SaleDepositResponse[]>([]);
  // Server-computed over the whole FILTERED set, not the page — see FooterSummary.
  const [totals, setTotals] = useState<SaleDepositTotals | null>(null);

  // สลิป viewer + หมายเหตุ modal. The slip viewer keys off the row's uuid rather than a
  // snapshot of its slips, so a corrected amount is reflected as soon as the list refetches.
  const [slipTargetUuid, setSlipTargetUuid] = useState<string | null>(null);
  const [noteTarget, setNoteTarget] = useState<SaleDepositResponse | null>(null);
  // The row BO is attaching slips to on the salesperson's behalf.
  const [addSlipsTarget, setAddSlipsTarget] =
    useState<SaleDepositResponse | null>(null);
  // Expenses ride inside the row already, so the drill-down needs no fetch — but key off the
  // uuid so a refetch (e.g. after a slip correction) refreshes the open breakdown too.
  const [expenseTargetUuid, setExpenseTargetUuid] = useState<string | null>(
    null,
  );

  const expenseTarget = useMemo(
    () => rows.find((row) => row.uuid === expenseTargetUuid) ?? null,
    [rows, expenseTargetUuid],
  );

  const slipTarget = useMemo(
    () => rows.find((row) => row.uuid === slipTargetUuid) ?? null,
    [rows, slipTargetUuid],
  );
  const slipItems = useMemo(
    () => (slipTarget ? normalizeTransferSlips(slipTarget) : []),
    [slipTarget],
  );

  const changeDateRange = useCallback((from: Dayjs, to: Dayjs) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  // The row whose outstanding QR bills are being listed.
  const [qrTarget, setQrTarget] = useState<SaleDepositResponse | null>(null);
  // The row whose uncollected-cash bills are being listed.
  const [cashTarget, setCashTarget] = useState<SaleDepositResponse | null>(null);
  // The row (and which sign-off) waiting on "there is still money outstanding — tick anyway?".
  const [reviewConfirm, setReviewConfirm] = useState<{
    row: SaleDepositResponse;
    side: DepositReviewSide;
  } | null>(null);

  const setRowReview = useCallback((uuid: string, patch: DepositReviewPatch) => {
    setRows((prev) =>
      prev.map((row) => (row.uuid === uuid ? { ...row, ...patch } : row)),
    );
  }, []);

  const setRowNote = useCallback((uuid: string, note: string | null) => {
    setRows((prev) =>
      prev.map((row) => (row.uuid === uuid ? { ...row, note } : row)),
    );
  }, []);

  const { reviewLoading, toggleReview, noteSaving, saveNote } =
    useDepositActions(setRowReview, setRowNote);

  // What the list is narrowed to. Also the identity the selection is cleared on: a set
  // picked under one filter would otherwise be signed off after the rows had scrolled away.
  const filter = useMemo<SaleDepositReportFilter>(
    () => ({
      dateFrom: dateFrom.format(API_DATE_FORMAT),
      dateTo: dateTo.format(API_DATE_FORMAT),
      // A pinned view names its branch in the URL, so it always sends it and lets the
      // server's tenant scope refuse a branch the caller may not read — the ปิดศูนย์เซล panel
      // below queries that same branch, and the two must not disagree. Without a pin,
      // branch-office users are scoped from the JWT: ignore a hand-edited branch_code param
      // and do not send it back to the API.
      branchCode:
        Boolean(branchDay) || canSelectBranch ? selectedBranch : undefined,
      routeCode: selectedRoute,
      status,
    }),
    [
      dateFrom,
      dateTo,
      branchDay,
      canSelectBranch,
      selectedBranch,
      selectedRoute,
      status,
    ],
  );

  // A closing covers ONE branch-day. On the drill-down the URL already names it, so the panel
  // is always on; on the standalone report it appears only once the filter has narrowed to a
  // single branch and a single day — wider than that, its figures would be meaningless.
  const closingBranchDay = useMemo(
    () =>
      branchDay ??
      resolveClosingBranchDay({
        dateFrom,
        dateTo,
        canSelectBranch,
        selectedBranch,
        rows,
      }),
    [branchDay, dateFrom, dateTo, canSelectBranch, selectedBranch, rows],
  );

  /**
   * The branch-day's closing state, published by the panel below rather than fetched again.
   * It is what tells a row whether it is frozen — and therefore whether its ตรวจแล้ว may
   * still move, and whether the branch may ask for it to be reopened at all.
   */
  const [closingSummary, setClosingSummary] =
    useState<SaleCenterClosingSummary | null>(null);
  // Identifies the branch-day the summary in hand describes. The panel is keyed by it and
  // republishes on mount, so what needs clearing here is the case where there is no panel
  // any more: a filter widened past one branch-day must not leave the previous day's closing
  // state deciding whether rows may be asked about.
  const closingBranchDayKey = closingBranchDay
    ? `${closingBranchDay.branchCode}-${closingBranchDay.businessDate}`
    : null;
  const { pendingDepositUuids, submitting: editRequestSubmitting, submitRequest } =
    useDepositEditRequests(closingBranchDay, canRequestEdit);
  const [editRequestTarget, setEditRequestTarget] =
    useState<SaleDepositResponse | null>(null);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSaleDepositReport(page, limit, filter);
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
      // Absent on a deployment predating the footer rollout — render nothing rather than a
      // client-side sum, which would silently total only the visible page.
      setTotals(data?.totals ?? null);
    } catch {
      setRows([]);
      setTotalCount(0);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filter]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  // Bumped by every write below that lands on these rows, and published to the ปิดศูนย์เซล
  // panel. The panel is counted from the same rows — how many of them each side has ticked,
  // the ยอดที่ต้องโอนรวม in its header, whether the day may be closed — so it has to move when
  // they do instead of waiting for the supervisor to reload the page by hand.
  const [depositsRevision, setDepositsRevision] = useState<number>(0);

  const markDepositsChanged = useCallback(() => {
    setDepositsRevision((revision) => revision + 1);
  }, []);

  /** A write the server accepted: reload the rows, and the summary counted from them. */
  const reloadDeposits = useCallback(async () => {
    markDepositsChanged();
    await fetchDeposits();
  }, [markDepositsChanged, fetchDeposits]);

  useEffect(() => {
    setClosingSummary(null);
  }, [closingBranchDayKey]);

  // Reset to first page whenever a filter changes the result set.
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // A selection only means anything against the rows it was made on. Once the filter, the
  // page or the page size moves, those rows are off screen and signing them off would be a
  // write the reviewer can no longer see the subject of.
  useEffect(() => {
    setSelectedUuids([]);
  }, [filter, page, limit]);

  // Curried so each row binds its own uuid without an inline arrow in JSX.
  const openExpenses = (uuid: string) => () => setExpenseTargetUuid(uuid);
  const openSlips = (uuid: string) => () => setSlipTargetUuid(uuid);
  const openNote = (row: SaleDepositResponse) => () => setNoteTarget(row);
  const openAddSlips = (row: SaleDepositResponse) => () => setAddSlipsTarget(row);
  const closeAddSlips = () => setAddSlipsTarget(null);
  /**
   * Ticking ตรวจแล้ว, with one interruption and only one.
   *
   * The confirm appears solely when money is still outstanding — and only on the way IN.
   * A row with nothing outstanding, or one being un-ticked, goes straight through.
   * Prompting on every tick would become something people click past without reading
   * within two days, which is worse than not prompting at all.
   */
  /**
   * One row's ตรวจแล้ว, and the panel below re-read as soon as the server takes it — the
   * sign-off it counts is the one just written. A refused write changes nothing, so it
   * leaves the panel alone.
   */
  const writeReview = async (
    row: SaleDepositResponse,
    side: DepositReviewSide,
    reviewed: boolean,
  ) => {
    const saved = await toggleReview(row.uuid, side, reviewed);
    if (saved) markDepositsChanged();
  };

  const changeReviewed =
    (row: SaleDepositResponse, side: DepositReviewSide) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      // Either kind of unfinished money earns the interruption. They mean different things
      // — one is missing from a total, the other is inside one nobody confirmed — but the
      // decision being asked for is the same: sign off now, or look first.
      if (checked && (row.outstanding_qr_count > 0 || row.outstanding_cash_count > 0)) {
        setReviewConfirm({ row, side });
        return;
      }
      writeReview(row, side, checked);
    };

  /** Which kinds of unfinished money this row has, so the confirm can name them. */
  const reviewConfirmKinds: "qr" | "cash" | "both" | null = !reviewConfirm
    ? null
    : reviewConfirm.row.outstanding_qr_count > 0 &&
        reviewConfirm.row.outstanding_cash_count > 0
      ? "both"
      : reviewConfirm.row.outstanding_qr_count > 0
        ? "qr"
        : "cash";

  const confirmReview = () => {
    if (!reviewConfirm) return;
    writeReview(reviewConfirm.row, reviewConfirm.side, true);
    setReviewConfirm(null);
  };

  // "ตรวจสอบก่อน" — hand them the list instead of the signature. QR wins when a row has
  // both, because it is the one with something to press: a bank check can clear it from
  // this screen, where the cash list can only be read.
  const reviewConfirmToList = () => {
    if (!reviewConfirm) return;
    if (reviewConfirm.row.outstanding_qr_count > 0) {
      setQrTarget(reviewConfirm.row);
    } else {
      setCashTarget(reviewConfirm.row);
    }
    setReviewConfirm(null);
  };

  const openEditRequest = (row: SaleDepositResponse) => () =>
    setEditRequestTarget(row);
  const closeEditRequest = () => setEditRequestTarget(null);

  const handleConfirmEditRequest = async (reason: string) => {
    if (!editRequestTarget) return;
    const submitted = await submitRequest(editRequestTarget.uuid, reason);
    // Left open on a refusal so the reason the branch typed survives a retry.
    if (submitted) setEditRequestTarget(null);
  };

  /**
   * What this row's ขอแก้ไข can do — raise the request, or say why it cannot. Resolved per
   * row rather than once, because only the pending-request half varies between rows: the
   * branch-day's closing state and its seven-day window are the same for all of them.
   */
  const editRequestOfferFor = (row: SaleDepositResponse): EditRequestOffer =>
    editRequestOffer({
      closingStatus: closingSummary?.status ?? null,
      closedAt: closingSummary?.closed_at ?? null,
      hasPendingRequest: pendingDepositUuids.includes(row.uuid),
    });

  // A closed branch-day is frozen: the supervisor signed it on how many routes each side had
  // ticked, so no ตรวจแล้ว on it may move any more.
  const branchDayClosed = closingSummary?.status === "closed";

  /** Why this row's own ตรวจแล้ว boxes are dead, or null when they can be ticked. */
  const reviewBlockedFor = (row: SaleDepositResponse): string | null =>
    reviewBlockedReason({
      row,
      reviewSaving: reviewLoading[row.uuid],
      branchDayClosed,
    });

  /**
   * Why each row on the page cannot be picked for a bulk sign-off, keyed by uuid and absent
   * when it can. Resolved in one pass because the select-all reads the same answer as the
   * row boxes do — "every selectable row is selected" has to be counted over this set.
   */
  const selectionBlockedByUuid = useMemo(() => {
    const reasons = new Map<string, string>();
    rows.forEach((row) => {
      const reason = selectionBlockedReason({
        row,
        reviewSaving: reviewLoading[row.uuid],
        branchDayClosed,
        side: reviewSide,
      });
      if (reason) reasons.set(row.uuid, reason);
    });
    return reasons;
  }, [rows, reviewLoading, branchDayClosed, reviewSide]);

  /** Rows on this page that a bulk ตรวจแล้ว tick would actually change. */
  const selectableUuids = useMemo(
    () =>
      rows
        .filter((row) => !selectionBlockedByUuid.has(row.uuid))
        .map((row) => row.uuid),
    [rows, selectionBlockedByUuid],
  );

  // A row that stops being selectable — this caller just ticked it, or the branch-day was
  // closed underneath the page — leaves the selection with it. Left standing it would keep
  // being counted by the button and by the select-all, for a write it can no longer earn.
  useEffect(() => {
    setSelectedUuids((previous) => {
      const stillSelectable = previous.filter((uuid) =>
        selectableUuids.includes(uuid),
      );
      return stillSelectable.length === previous.length
        ? previous
        : stillSelectable;
    });
  }, [selectableUuids]);

  const allRowsSelected =
    selectableUuids.length > 0 &&
    selectedUuids.length === selectableUuids.length;
  const someRowsSelected = selectedUuids.length > 0 && !allRowsSelected;

  const toggleSelectAll = () => {
    setSelectedUuids(allRowsSelected ? [] : selectableUuids);
  };

  // Curried so each row binds its own uuid without an inline arrow in JSX.
  const toggleRowSelected = (uuid: string) => () => {
    setSelectedUuids((previous) =>
      previous.includes(uuid)
        ? previous.filter((selected) => selected !== uuid)
        : [...previous, uuid],
    );
  };

  // The rows just signed off carry new ticks and reviewer names, and the selection that
  // produced them has been spent.
  const handleBulkReviewed = async () => {
    setSelectedUuids([]);
    await reloadDeposits();
  };

  const closeSlips = () => setSlipTargetUuid(null);
  const closeExpenses = () => setExpenseTargetUuid(null);
  const closeNote = () => setNoteTarget(null);

  // The selection and จัดการ columns each exist only for their own cohort; everything that
  // spans the table's width — the skeleton, the empty row, the totals footer — counts from
  // here so the widths cannot drift apart.
  const columnCount =
    BASE_COLUMN_COUNT + (canBulkReview ? 1 : 0) + (canRequestEdit ? 1 : 0);

  // The selection column is pinned ahead of วันที่, so it pushes the two pinned columns along.
  const dateColumnOffset = canBulkReview ? SELECT_COLUMN_WIDTH : 0;
  const routeColumnOffset = dateColumnOffset + DATE_COLUMN_WIDTH;

  const handleSaveNote = async (note: string) => {
    if (!noteTarget) return;
    const ok = await saveNote(noteTarget.uuid, note.trim() ? note : null);
    if (ok) setNoteTarget(null);
  };

  return (
    <>
      <Paper>
        <SaleDepositFilter
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChangeDateRange={changeDateRange}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
          selectedRoute={selectedRoute}
          setSelectedRoute={setSelectedRoute}
          status={status}
          setStatus={setStatus}
          isBranchDayPinned={Boolean(branchDay)}
        />
        {/* Appears with the first ticked row and names how many are selected — see
            BulkReviewAction. Only a reviewer who owns a ตรวจแล้ว box has a selection to
            act on, which is the same condition the selection column itself is on. */}
        {canBulkReview && (
          <BulkReviewAction
            selectedUuids={selectedUuids}
            onCompleted={handleBulkReviewed}
          />
        )}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                "& th": {
                  bgcolor: (theme) => theme.palette.background.default,
                  color: (theme) => theme.palette.gray.main,
                  fontWeight: 600,
                },
              }}
            >
              <TableRow sx={{ whiteSpace: "nowrap" }}>
                {canBulkReview && (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      ...stickyColumnSx(0, STICKY_HEADER_Z_INDEX),
                      width: SELECT_COLUMN_WIDTH,
                    }}
                  >
                    {/* Covers the SELECTABLE rows on screen only — the pages behind this
                        one are not loaded, and a box that silently reached them would sign
                        off rows nobody has looked at. With nothing on the page left to sign
                        there is no set to pick, so the box goes dead with the rows. */}
                    <Checkbox
                      checked={allRowsSelected}
                      indeterminate={someRowsSelected}
                      disabled={selectableUuids.length === 0}
                      onChange={toggleSelectAll}
                      inputProps={{ "aria-label": SELECT_ALL_LABEL }}
                    />
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    ...stickyColumnSx(dateColumnOffset, STICKY_HEADER_Z_INDEX),
                    minWidth: DATE_COLUMN_WIDTH,
                  }}
                >
                  วันที่
                </TableCell>
                <TableCell
                  align="center"
                  sx={stickyColumnSx(routeColumnOffset, STICKY_HEADER_Z_INDEX)}
                >
                  สายส่ง
                </TableCell>
                <TableCell align="center">สาขา</TableCell>
                <TableCell align="center">จำนวนร้านค้า</TableCell>
                <TableCell align="right">ยอดรวม</TableCell>
                <TableCell align="right">ยอดเงินเชื่อ</TableCell>
                {/* Cash in hand, whole baht — รวมเงินสด below is this plus เงินโอน. The hint
                    is what stops "ทำไมไม่มีสตางค์" from reading as a rounding bug. */}
                <TableCell align="right">
                  <Tooltip title={CASH_IN_HAND_HINT}>
                    <Box component="span" sx={{ borderBottom: "1px dotted", cursor: "help" }}>
                      {CASH_IN_HAND_LABEL}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">เงินโอน</TableCell>
                <TableCell align="right">รวมเงินสด</TableCell>
                <TableCell align="right">ยอดฝากเก็บ</TableCell>
                <TableCell align="right">ค่าใช้จ่าย</TableCell>
                <TableCell align="right">ที่ต้องโอน/ยอดโอน</TableCell>
                <TableCell align="right">ผลต่าง</TableCell>
                <TableCell>วัน เวลา การโอน</TableCell>
                <TableCell align="center">สลิป</TableCell>
                <TableCell align="center">ตรวจแล้ว (BO)</TableCell>
                <TableCell align="center">ตรวจแล้ว (HO)</TableCell>
                <TableCell align="center">หมายเหตุ</TableCell>
                {canRequestEdit && (
                  <TableCell align="center">{ROW_ACTIONS_HEADER}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableSkeletonRows
                  rowCount={limit}
                  columnCount={columnCount}
                />
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row, index) => (
                  <TableRow
                    key={row.uuid}
                    sx={{
                      bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      "& td, & th": { padding: "8px" },
                    }}
                  >
                    {canBulkReview && (
                      <SelectRowCheckboxCell
                        checked={selectedUuids.includes(row.uuid)}
                        blockedReason={
                          selectionBlockedByUuid.get(row.uuid) ?? null
                        }
                        label={selectRowLabel(row.route_code)}
                        onChange={toggleRowSelected(row.uuid)}
                      />
                    )}
                    <TableCell
                      align="center"
                      sx={stickyColumnSx(dateColumnOffset, STICKY_BODY_Z_INDEX)}
                    >
                      {formatDay(row.business_date)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={stickyColumnSx(routeColumnOffset, STICKY_BODY_Z_INDEX)}
                    >
                      {row.route_code}
                    </TableCell>
                    <TableCell align="center">{row.branch_code || "-"}</TableCell>
                    <TableCell align="center">
                      {numberWithCommas(row.customer_count)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.total_amount)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.credit_total_value)}
                    </TableCell>
                    {/* Same treatment as the เงินโอน cell below, for the opposite reason:
                        this money IS in the figure, and the marker says how much of it
                        nobody confirmed receiving. A clean row renders exactly as before. */}
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {formatCashInHand(physicalCash(row))}
                        {row.outstanding_cash_count > 0 && (
                          <Tooltip
                            title={`ยังไม่ยืนยันการรับชำระ ${row.outstanding_cash_count} ใบ · ${formatNumber(
                              row.outstanding_cash_amount,
                            )} — รวมอยู่ในยอดนี้แล้ว`}
                          >
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => setCashTarget(row)}
                            >
                              <ErrorOutlineIcon
                                sx={{ fontSize: 16, color: "warning.main" }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {/* The marker lives in this cell rather than a column of its own:
                        this is the figure the outstanding money is missing from, and the
                        table already carries seventeen columns. Rows with nothing
                        outstanding render exactly as before — no icon, no reserved gap. */}
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {formatNumber(row.transfer_total_value)}
                        {row.outstanding_qr_count > 0 && (
                          <Tooltip
                            title={`QR ค้าง ${row.outstanding_qr_count} ใบ · ${formatNumber(
                              row.outstanding_qr_amount,
                            )} — ยังไม่รวมในยอดนี้`}
                          >
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => setQrTarget(row)}
                            >
                              <ErrorOutlineIcon
                                sx={{ fontSize: 16, color: "warning.main" }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.cash_total_value)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(row.deposit_collection_amount)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="inherit"
                        onClick={openExpenses(row.uuid)}
                        sx={{
                          minWidth: 0,
                          p: 0,
                          color: "link.main",
                          textDecoration: "underline",
                          fontWeight: 500,
                          "&:hover": {
                            textDecoration: "underline",
                            bgcolor: "transparent",
                          },
                        }}
                      >
                        {formatNumber(row.total_expense_amount)}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <TransferAmountText
                        required={row.required_transfer_amount}
                        transfer={row.transfer_amount}
                      />
                      <NetOfNotesHint adjustment={row.credit_debit_note_adjustment} />
                    </TableCell>
                    <TableCell align="right">
                      <TransferDiffText
                        required={row.required_transfer_amount}
                        transfer={row.transfer_amount}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDay(row.transfer_date)}
                        </Typography>
                        {row.transfer_time && (
                          <Typography variant="caption" color="text.secondary">
                            {row.transfer_time} น.
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                        {(row.slips?.length ?? 0) > 0 || row.slip_url ? (
                          <Tooltip title="ดูสลิป">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={openSlips(row.uuid)}
                            >
                              <ReceiptLongIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="ไม่มีสลิป">
                            <span>
                              <IconButton size="small" disabled>
                                <ReceiptLongIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {/* Attaching on the branch's behalf is refused by the server
                            only once the branch-day is closed (ปิดศูนย์เซล) — a ตรวจแล้ว
                            tick locks nothing. That closed state belongs to the branch-day,
                            not the row, and a list row carries no flag for it, so the button
                            is offered on the role alone and the server's refusal is the
                            enforcement. */}
                        {canUploadSlipOnBehalf && (
                          <Tooltip title="แนบสลิปแทนพนักงานขาย">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={openAddSlips(row)}
                            >
                              <AddPhotoAlternateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {/* The two sign-offs are independent: each side ticks when it gets to
                        the row, in either order, and neither box gates or freezes the other.
                        What is left disabling a box is the role that does not own it, and
                        the row-level rules the selection column reads from the same place. */}
                    <ReviewCheckboxCell
                      checked={row.bo_reviewed}
                      reviewerName={row.bo_reviewed_by_name}
                      reviewedAt={row.bo_reviewed_at}
                      disabled={!canReviewAsBo}
                      disabledReason={canReviewAsBo ? reviewBlockedFor(row) : null}
                      onChange={changeReviewed(row, "bo")}
                    />
                    <ReviewCheckboxCell
                      checked={row.ho_reviewed}
                      reviewerName={row.ho_reviewed_by_name}
                      reviewedAt={row.ho_reviewed_at}
                      disabled={!canReviewAsHo}
                      disabledReason={canReviewAsHo ? reviewBlockedFor(row) : null}
                      onChange={changeReviewed(row, "ho")}
                    />
                    <TableCell>
                      <NoteCell
                        note={row.note}
                        onEdit={openNote(row)}
                      />
                    </TableCell>
                    {canRequestEdit && (
                      <EditRequestCell
                        offer={editRequestOfferFor(row)}
                        onRequest={openEditRequest(row)}
                      />
                    )}
                  </TableRow>
                ))}
            </TableBody>
            <FooterSummary
              totals={totals}
              loading={loading}
              rowCount={totalCount}
              columnCount={columnCount}
            />
          </Table>
        </TableContainer>
        <ReportPagination
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </Paper>

      {closingBranchDay && (
        // Keyed by the branch-day so a switch of branch or date starts a fresh panel: the
        // cash figure is typed against ONE day's totals and must never survive into another.
        <SaleCenterClosingPanel
          key={closingBranchDayKey}
          branchCode={closingBranchDay.branchCode}
          businessDate={closingBranchDay.businessDate}
          // The panel redraws itself from the answer to its own close or send-back, so this
          // reloads the rows alone — routing it through reloadDeposits would only make the
          // panel re-read what it has just been told.
          onDepositsChanged={fetchDeposits}
          onSummaryChange={setClosingSummary}
          depositsRevision={depositsRevision}
        />
      )}

      <TransferSlipsModal
        open={slipTarget !== null}
        slips={slipItems}
        depositUuid={slipTarget?.uuid}
        // Correcting is a finance/accounting action. The server's only other lock is the
        // branch-day being closed, which a list row cannot report, so the pencil follows the
        // role alone and a closed day is refused server-side.
        canEditAmount={canEditSlipAmount}
        onAmountUpdated={reloadDeposits}
        onClose={closeSlips}
      />
      <AddSlipsDialog
        open={addSlipsTarget !== null}
        depositUuid={addSlipsTarget?.uuid ?? null}
        existingSlipCount={addSlipsTarget?.slips?.length ?? 0}
        onClose={closeAddSlips}
        onUploaded={reloadDeposits}
      />
      <ExpenseDetailModal
        open={expenseTarget !== null}
        deposit={expenseTarget}
        canEdit={canEditExpenses}
        // The server recomputes ที่ต้องโอน and ผลต่าง from the new expenses, so the row is
        // reloaded rather than patched locally — same as a slip-amount correction.
        onSaved={reloadDeposits}
        onClose={closeExpenses}
      />
      <DepositNoteModal
        open={Boolean(noteTarget)}
        note={noteTarget?.note}
        loading={noteSaving}
        onClose={closeNote}
        onSave={handleSaveNote}
      />
      <OutstandingQrModal
        open={qrTarget !== null}
        depositUuid={qrTarget?.uuid ?? null}
        routeCode={qrTarget?.route_code ?? ""}
        businessDate={qrTarget?.business_date ?? ""}
        onClose={() => setQrTarget(null)}
        // A settled bill changes the row's figure, and settling one usually means there
        // are others — so the list is reloaded rather than the modal closed.
        onSettled={reloadDeposits}
      />
      <EditRequestDialog
        open={editRequestTarget !== null}
        deposit={editRequestTarget}
        saving={editRequestSubmitting}
        onClose={closeEditRequest}
        onConfirm={handleConfirmEditRequest}
      />
      <UncollectedCashModal
        open={cashTarget !== null}
        depositUuid={cashTarget?.uuid ?? null}
        routeCode={cashTarget?.route_code ?? ""}
        businessDate={cashTarget?.business_date ?? ""}
        onClose={() => setCashTarget(null)}
      />
      {/* Warns, never blocks. The reviewer may sign off knowing the total can still move;
          what they may not do is sign off without being told. */}
      <Dialog
        open={reviewConfirm !== null}
        onClose={() => setReviewConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        {/* Titled by what is actually outstanding. A QR-only row keeps the wording it has
            always had; naming both kinds on every row would make the QR case vaguer for
            the sake of the cash one. */}
        <DialogTitle sx={{ fontWeight: 700 }}>
          {reviewConfirmKinds === "qr"
            ? "ยังมี QR ค้าง"
            : reviewConfirmKinds === "cash"
              ? "ยังมีบิลที่ยังไม่ยืนยันการรับชำระ"
              : "ยังมียอดค้างอยู่"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" gutterBottom>
              สายส่ง {reviewConfirm?.row.route_code} วันที่{" "}
              {reviewConfirm?.row.business_date}
            </Typography>
            {(reviewConfirm?.row.outstanding_qr_count ?? 0) > 0 && (
              <>
                <Typography variant="body2" gutterBottom>
                  QR ค้าง{" "}
                  <strong>
                    {reviewConfirm?.row.outstanding_qr_count} ใบ
                  </strong>{" "}
                  รวม{" "}
                  <strong>
                    {formatNumber(reviewConfirm?.row.outstanding_qr_amount ?? 0)}
                  </strong>{" "}
                  บาท ที่ยังไม่ถูกนับในยอดเงินโอน
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  หากเงินเข้าภายหลัง ยอดเงินโอนของวันนี้จะเพิ่มขึ้น
                  แม้จะกดตรวจแล้วไปแล้ว
                </Typography>
              </>
            )}
            {(reviewConfirm?.row.outstanding_cash_count ?? 0) > 0 && (
              <>
                <Typography variant="body2" gutterBottom>
                  ยังไม่ยืนยันการรับชำระ{" "}
                  <strong>
                    {reviewConfirm?.row.outstanding_cash_count} ใบ
                  </strong>{" "}
                  รวม{" "}
                  <strong>
                    {formatNumber(
                      reviewConfirm?.row.outstanding_cash_amount ?? 0,
                    )}
                  </strong>{" "}
                  บาท
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ยอดนี้รวมอยู่ในเงินสดในมือแล้ว
                  และพนักงานขายต้องนำส่งตามยอดนี้
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReviewConfirm(null)} color="inherit">
            ยกเลิก
          </Button>
          <Button onClick={reviewConfirmToList}>ตรวจสอบก่อน</Button>
          <Button onClick={confirmReview} variant="contained">
            ยืนยัน ตรวจแล้ว
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
